import { useState } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { SquareKanban, ListChecks, ShieldQuestion, MoreHorizontal, Users, Upload, TrendingUp, type LucideIcon, Clapperboard, Download, ClipboardCheck } from 'lucide-react';

import { Avatar, AvatarFallback } from '../../../components/ui/avatar';
import { cn } from '../../../components/ui/utils';
import { ROUTES, isSectionActive, orderedNavSections } from '../../../shared/config/routes';
import { useVisibleNavSections } from '../../../entities/pipeline';
import { initialsOf, loginOf, useSignedInEmail } from '../../../shared/hooks/useSignedInEmail';
import { ProfileDialog } from '../../profile-dialog';
import { PushToggle, useHasPush } from '../../../features/montage/components/PushToggle';
import {
  Sheet, SheetContent, SheetHeader, SheetTitle,
} from '../../../components/ui/sheet';

/**
 * Сколько разделов помещается внизу.
 *
 * Четыре: три раздела плюс «Ещё». У контент-мейкера разделов шесть, и на
 * 390px подписи начинали наезжать друг на друга — нижняя панель переставала
 * читаться ровно там, где она и нужна, на телефоне.
 */
const VISIBLE_TABS = 3;

const SECTION_ICONS: Record<string, LucideIcon> = {
  [ROUTES.MONTAGE]: SquareKanban,
  [ROUTES.MY_TASKS]: ListChecks,
  [ROUTES.VIDEOS]: Clapperboard,
  [ROUTES.TAKES]: Download,
  [ROUTES.WORK]: ClipboardCheck,
  [ROUTES.MODERATION]: ShieldQuestion,
  [ROUTES.STATS]: TrendingUp,
  [ROUTES.PUBLICATIONS]: Upload,
  [ROUTES.ACCOUNTS]: Users,
};

/** Общий вид пункта: иконка сверху, подпись снизу. */
const TAB = 'flex flex-1 flex-col items-center gap-1 py-2.5 text-xs font-medium transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50';

/**
 * Нижняя навигация для телефона: большой палец до сайдбара не достаёт,
 * поэтому основные переходы живут внизу.
 *
 * Последний пункт — не раздел, а аватарка: она открывает окно профиля,
 * как кружок аккаунта в Claude.
 */
export function MobileTabBar(): JSX.Element {
  const { pathname } = useLocation();
  const email = useSignedInEmail();
  const [profileOpen, setProfileOpen] = useState(false);
  // Тот же порядок, что в сайдбаре: первая вкладка внизу = первая строка меню.
  const sections = orderedNavSections(useVisibleNavSections() ?? []);
  const [moreOpen, setMoreOpen] = useState(false);
  // Кнопка «Пуши» занимает место одного раздела: шестой пункт на 390px не читается.
  const hasPush = useHasPush();
  const visible = hasPush && sections.length > VISIBLE_TABS ? VISIBLE_TABS - 1 : VISIBLE_TABS;
  const primary = sections.slice(0, visible);
  const rest = sections.slice(visible);

  return (
    <>
      <nav
        aria-label="Разделы"
        className="fixed inset-x-0 bottom-0 z-40 border-t bg-background/95 backdrop-blur md:hidden"
        style={{ paddingBottom: 'env(safe-area-inset-bottom, 0px)' }}
      >
        {/* flex, а не grid-cols-N: количество вкладок задаётся данными */}
        <ul className="flex">
          {primary.map((section) => {
            const Icon = SECTION_ICONS[section.path] ?? Users;
            const active = isSectionActive(pathname, section.match, (section as { end?: boolean }).end);

            return (
              <li key={section.path} className="flex flex-1">
                <Link
                  to={section.path}
                  aria-current={active ? 'page' : undefined}
                  className={cn(
                    TAB,
                    active ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  <Icon className="size-5" />
                  {section.label}
                </Link>
              </li>
            );
          })}

          {rest.length > 0 && (
            <li className="flex flex-1">
              <button
                type="button"
                onClick={() => setMoreOpen(true)}
                aria-haspopup="dialog"
                aria-expanded={moreOpen}
                className={cn(
                  TAB,
                  rest.some((s) => isSectionActive(pathname, s.match, (s as { end?: boolean }).end))
                    ? 'text-primary'
                    : 'text-muted-foreground hover:text-foreground',
                )}
              >
                <MoreHorizontal className="size-5" />
                Ещё
              </button>
            </li>
          )}

          <PushToggle>
            {({ icon: Icon, label, on, busy, onClick }) => (
              <li className="flex flex-1">
                <button
                  type="button"
                  onClick={onClick}
                  disabled={busy}
                  aria-pressed={on}
                  className={cn(TAB, on ? 'text-primary' : 'text-muted-foreground hover:text-foreground')}
                >
                  <Icon className="size-5" />
                  {label}
                </button>
              </li>
            )}
          </PushToggle>

          <li className="flex flex-1">
            <button
              type="button"
              onClick={() => setProfileOpen(true)}
              aria-haspopup="dialog"
              aria-expanded={profileOpen}
              className={cn(
                TAB,
                profileOpen ? 'text-primary' : 'text-muted-foreground hover:text-foreground',
              )}
            >
              <Avatar className="size-5">
                <AvatarFallback
                  className={cn(
                    'text-[9px] font-semibold transition-colors',
                    profileOpen && 'bg-primary text-primary-foreground',
                  )}
                >
                  {initialsOf(email ? loginOf(email) : '?')}
                </AvatarFallback>
              </Avatar>
              Профиль
            </button>
          </li>
        </ul>
      </nav>

      <Sheet open={moreOpen} onOpenChange={setMoreOpen}>
        <SheetContent side="bottom" className="gap-0">
          <SheetHeader>
            <SheetTitle className="text-left">Разделы</SheetTitle>
          </SheetHeader>
          <ul className="flex flex-col px-2 pb-6">
            {rest.map((section) => {
              const Icon = SECTION_ICONS[section.path] ?? Users;
              const active = isSectionActive(pathname, section.match, (section as { end?: boolean }).end);
              return (
                <li key={section.path}>
                  <Link
                    to={section.path}
                    onClick={() => setMoreOpen(false)}
                    aria-current={active ? 'page' : undefined}
                    className={cn(
                      'flex items-center gap-3 rounded-lg px-3 py-3 text-sm font-medium transition-colors',
                      active ? 'bg-accent text-primary' : 'hover:bg-accent/50',
                    )}
                  >
                    <Icon className="size-5" />
                    {section.label}
                  </Link>
                </li>
              );
            })}
          </ul>
        </SheetContent>
      </Sheet>

      <ProfileDialog open={profileOpen} onOpenChange={setProfileOpen} />
    </>
  );
}
